// Procedural binaural spatial audio engine (Web Audio API, zero payload).
// Left ear  -> metallic analytical chimes.
// Right ear -> organic bass tones that intensify with wager size.

class AudioEngine {
  constructor() {
    this.ctx = null;
    this.started = false;
    this.intensity = 0;
    this._chimeTimer = null;
    this.isMuted = false;
  }

  ensure() {
    if (this.started) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    const ctx = this.ctx;

    this.master = ctx.createGain();
    this.master.gain.value = 0.0001;
    this.master.connect(ctx.destination);
    this.master.gain.exponentialRampToValueAtTime(this.isMuted ? 0.0001 : 0.9, ctx.currentTime + 1.6);

    this._setupBass();
    this._setupChimes();
    this.started = true;
  }

  setMuted(m) {
    this.isMuted = m;
    if (this.master && this.ctx) {
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setValueAtTime(Math.max(0.0001, this.master.gain.value), t);
      this.master.gain.exponentialRampToValueAtTime(m ? 0.0001 : 0.9, t + 0.3);
    }
  }

  toggleMute() {
    this.setMuted(!this.isMuted);
    return this.isMuted;
  }

  resume() {
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
  }

  _panner(x) {
    const p = this.ctx.createPanner();
    p.panningModel = 'HRTF';
    p.distanceModel = 'inverse';
    try {
      p.positionX.value = x;
      p.positionY.value = 0;
      p.positionZ.value = 0.2;
    } catch (e) {
      p.setPosition(x, 0, 0.2);
    }
    return p;
  }

  _setupBass() {
    const ctx = this.ctx;
    this.bassGain = ctx.createGain();
    this.bassGain.gain.value = 0.12;
    const p = this._panner(1.4); // right ear
    this.bassPanner = p;

    const o1 = ctx.createOscillator();
    o1.type = 'sine';
    o1.frequency.value = 52;
    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.value = 78;
    const og2 = ctx.createGain();
    og2.gain.value = 0.28;

    // slow LFO for an organic swell
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.15;
    const lg = ctx.createGain();
    lg.gain.value = 6;
    lfo.connect(lg);
    lg.connect(o1.frequency);

    o1.connect(this.bassGain);
    o2.connect(og2);
    og2.connect(this.bassGain);
    this.bassGain.connect(p);
    p.connect(this.master);

    o1.start();
    o2.start();
    lfo.start();
    this.bassOsc = o1;
  }

  _setupChimes() {
    this._chimeLoop();
  }

  _chimeLoop() {
    if (!this.ctx) return;
    this._chime();
    const delay = 950 - this.intensity * 680 + Math.random() * 320;
    this._chimeTimer = setTimeout(() => this._chimeLoop(), Math.max(160, delay));
  }

  _chime() {
    if (this.isMuted) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const p = this._panner(-1.4); // left ear
    const freqs = [1046, 1318, 1568, 2093, 2637];
    const f = freqs[(Math.random() * freqs.length) | 0];

    const carrier = ctx.createOscillator();
    carrier.type = 'triangle';
    carrier.frequency.value = f;
    const mod = ctx.createOscillator();
    mod.type = 'square';
    mod.frequency.value = f * 2.01; // metallic FM
    const mg = ctx.createGain();
    mg.gain.value = f * 0.6;
    mod.connect(mg);
    mg.connect(carrier.frequency);

    const g = ctx.createGain();
    const amp = 0.04 + this.intensity * 0.12;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(amp, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);

    carrier.connect(g);
    g.connect(p);
    p.connect(this.master);
    carrier.start(t);
    mod.start(t);
    carrier.stop(t + 0.66);
    mod.stop(t + 0.66);
  }

  setIntensity(v) {
    this.intensity = v;
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.bassGain.gain.setTargetAtTime(0.12 + v * 0.55, now, 0.3);
    this.bassOsc.frequency.setTargetAtTime(52 - v * 12, now, 0.5);
  }

  clutch() {
    if (!this.ctx || this.isMuted) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const p = this._panner(0);
    p.connect(this.master);

    // rising tension drone with accelerating tremolo
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(320, t + 5);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(500, t);
    lp.frequency.exponentialRampToValueAtTime(2600, t + 5);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.12, t + 0.3);
    g.gain.setValueAtTime(0.13, t + 4.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 5.0);
    const trem = ctx.createOscillator();
    trem.frequency.setValueAtTime(4, t);
    trem.frequency.exponentialRampToValueAtTime(15, t + 5);
    const tg = ctx.createGain();
    tg.gain.value = 0.06;
    trem.connect(tg);
    tg.connect(g.gain);
    o.connect(lp);
    lp.connect(g);
    g.connect(p);
    o.start(t);
    o.stop(t + 5.05);
    trem.start(t);
    trem.stop(t + 5.05);

    // accelerating heartbeat thumps
    let tt = 0;
    while (tt < 5) {
      const bt = t + tt;
      const bo = ctx.createOscillator();
      bo.type = 'sine';
      bo.frequency.setValueAtTime(60, bt);
      bo.frequency.exponentialRampToValueAtTime(34, bt + 0.15);
      const bg = ctx.createGain();
      bg.gain.setValueAtTime(0.0001, bt);
      bg.gain.exponentialRampToValueAtTime(0.5, bt + 0.02);
      bg.gain.exponentialRampToValueAtTime(0.0001, bt + 0.22);
      bo.connect(bg);
      bg.connect(p);
      bo.start(bt);
      bo.stop(bt + 0.25);
      tt += 0.78 - (tt / 5) * 0.45;
    }
  }

  win(big = false) {
    if (!this.ctx || this.isMuted) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const p = this._panner(0);
    p.connect(this.master);
    const chord = big ? [523, 659, 784, 1046, 1319, 1568, 2093] : [523, 659, 784, 1046, 1568];
    const dur = big ? 3.4 : 2.6;
    const amp = big ? 0.16 : 0.12;
    chord.forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = big ? 6000 : 4200;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(amp, t + 0.05 + i * 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(lp);
      lp.connect(g);
      g.connect(p);
      o.start(t);
      o.stop(t + dur + 0.1);
    });
    if (big) {
      // shimmering reward bells
      for (let k = 0; k < 6; k++) {
        const bt = t + 0.1 + k * 0.12;
        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.value = 1568 * Math.pow(2, k / 12);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, bt);
        g.gain.exponentialRampToValueAtTime(0.1, bt + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, bt + 0.9);
        o.connect(g);
        g.connect(p);
        o.start(bt);
        o.stop(bt + 0.95);
      }
    }
  }

  loss() {
    if (!this.ctx || this.isMuted) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const p = this._panner(0);
    p.connect(this.master);

    // deep oppressive sub-bass drone
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(60, t);
    o.frequency.exponentialRampToValueAtTime(32, t + 1.4);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.7, t + 0.08);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
    o.connect(g);
    g.connect(p);
    o.start(t);
    o.stop(t + 2.0);

    // crimson noise crack
    const nb = ctx.createBufferSource();
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.4, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 2);
    nb.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 220;
    const ng = ctx.createGain();
    ng.gain.value = 0.3;
    nb.connect(bp);
    bp.connect(ng);
    ng.connect(p);
    nb.start(t);
  }

  // Soft two-tone bell used as a spatial cue for each Neuro-Tour placard.
  cue(freq = 880) {
    if (!this.ctx || this.isMuted) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const p = this._panner(0);
    p.connect(this.master);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.11, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
    g.connect(p);
    [freq, freq * 2.01].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      const og = ctx.createGain();
      og.gain.value = i === 0 ? 1 : 0.4;
      o.connect(og);
      og.connect(g);
      o.start(t);
      o.stop(t + 1.25);
    });
  }

  // Triumphant fanfare for the 5-win milestone celebration.
  milestone() {
    if (!this.ctx || this.isMuted) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const p = this._panner(0);
    p.connect(this.master);

    // ascending major arpeggio (C-E-G-C-E-G) building to a bright chord
    const seq = [523, 659, 784, 1046, 1319, 1568];
    seq.forEach((f, i) => {
      const bt = t + i * 0.11;
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 6500;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, bt);
      g.gain.exponentialRampToValueAtTime(0.16, bt + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, bt + 0.9);
      o.connect(lp);
      lp.connect(g);
      g.connect(p);
      o.start(bt);
      o.stop(bt + 0.95);
    });

    // sustained golden chord
    [784, 988, 1175, 1568].forEach((f) => {
      const bt = t + 0.66;
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, bt);
      g.gain.exponentialRampToValueAtTime(0.12, bt + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, bt + 2.6);
      o.connect(g);
      g.connect(p);
      o.start(bt);
      o.stop(bt + 2.7);
    });

    // shimmering reward bells
    for (let k = 0; k < 10; k++) {
      const bt = t + 0.7 + k * 0.09;
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = 1568 * Math.pow(2, k / 12);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, bt);
      g.gain.exponentialRampToValueAtTime(0.09, bt + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, bt + 0.8);
      o.connect(g);
      g.connect(p);
      o.start(bt);
      o.stop(bt + 0.85);
    }
  }
}

export const audio = new AudioEngine();
